import { TrueBase, css, html } from "../TrueBase/TrueBase";

/**
 * @customelement - true-slider
 * @description
 * A slider component.
 *
 * ### Properties
 *
 *
 */
export class TrueSlider extends TrueBase {
  static get properties() {
    return {
      gap: {
        type: Number,
      },
      slidesToShow: {
        type: Number,
      },
      slidesToScroll: {
        type: Number,
      },
      slideWidth: {
        type: Number,
        attribute: false,
        reflect: false,
      },
      slideTotal: {
        type: Number,
        attribute: false,
        reflect: false,
      },
      totalItems: {
        type: Number,
        attribute: false,
        reflect: false,
      },
      initialSettings: {
        type: Object,
        reflect: false,
        attributes: false,
      },
      activeIndex: {
        type: Number,
        reflect: false,
        attributes: false,
      },
      activeBreakpoint: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      responsive: {
        type: Array,
      },
      leftDisabled: {
        type: Boolean,
        reflect: false,
      },
      rightDisabled: {
        type: Boolean,
        reflect: false,
      },
      xPosition: {
        type: Number,
        reflect: false,
        attribute: false,
      },
      yPosition: {
        type: Number,
        reflect: false,
        attribute: false,
      },
      sliderWrap: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      nav: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      navNeeded: {
        type: Boolean,
        type: Object,
        reflect: false,
        attribute: false,
      },
      slides: {
        type: Array,
        reflect: false,
        attribute: false,
      },
      seperateNavs: {
        type: Boolean,
      },
      buttonLeftLabel: {
        type: String,
      },
      buttonRightLabel: {
        type: String,
      },
      slideChangedEvent: {
        type: CustomEvent,
        reflect: false,
        attribute: false,
      },
    };
  }

  constructor() {
    super();

    //defaults
    this.slidesToShow = 1;
    this.slidesToScroll = 1;
    this.fadeTransition = false;
    this.slideTotal = 0;
    this.activeIndex = 1;
    this.activeBreakpoint = {};
    this.buttonLeftLabel = "Prev";
    this.buttonRightLabel = "Next";
  }

  render() {
    return html`
      <div class="container">
        <div class="slider-wrap">
          <div
            class="slides"
            style="transform: translate3d(${this.slideTotal}px, 0 ,0)"
          >
            <slot id="slidesslot"></slot>
          </div>
        </div>
        ${this.seperateNavs
          ? html`
              <button
                aria-label="${this.buttonLeftLabel}"
                class="nav nav-left"
                @click="${this.prev}"
              >
                <slot name="sliderLeft"></slot>
              </button>
              <button
                aria-label="${this.buttonLeftLabel}"
                class="nav nav-right"
                @click="${this.next}"
              >
                <slot name="sliderRight"></slot>
              </button>
            `
          : html`
              <div class="slider-nav">
                <button
                  aria-label="${this.buttonLeftLabel}"
                  class="nav nav-left"
                  @click="${this.prev}"
                >
                  <slot name="sliderLeft"></slot>
                </button>
                <button
                  aria-label="${this.buttonLeftLabel}"
                  class="nav nav-right"
                  @click="${this.next}"
                >
                  <slot name="sliderRight"></slot>
                </button>
              </div>
            `}
      </div>
    `;
  }

  connectedCallback() {
    let resizeTimeOut;
    super.connectedCallback && super.connectedCallback();

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeOut);
      resizeTimeOut = setTimeout(() => {
        this._setSlideWidth();
        if (typeof this.responsive !== "undefined" && this.responsive.length) {
          this._checkResponsive();
        }
      }, 500);
    });
    this._checkForErrors();
  }

  /**
   * Checks if the node is of type HTMLSlotElement.
   * @constructor
   * @param {Node} node - The element to check for.
   */
  isSlot(node) {
    return node instanceof HTMLSlotElement;
  }

  /**
   * Returns the children of the slot.
   * If the child is a slot the it returns the children of that slot.
   * @constructor
   */
  _getChildElements(slot) {
    const slidesNodes = slot.assignedElements();
    return slidesNodes.map((child) =>
      this.isSlot(child) ? child.assignedElements() : child
    );
  }

  firstUpdated() {
    this.sliderWrap = this.shadowRoot.querySelector(".slides");
    this.nav = this.shadowRoot.querySelector(".slider-nav");
    const sliderSlot = this.shadowRoot.getElementById("slidesslot");
    this.slides = this._getChildElements(sliderSlot);
    this._setInitialSettings();
    if (typeof this.responsive !== "undefined" && this.responsive.length) {
      this._checkResponsive();
    }
    sliderSlot.addEventListener("slotchange", () => {
      // gets slider items
      this.totalItems =
        this.slides.length > 1 ? this.slides.length : this.slides[0].length;

      this.sliderWrap.addEventListener(
        "touchstart",
        function (event) {
          this._handleTouchStart(event);
        }.bind(this),
        { passive: true }
      );
      this.sliderWrap.addEventListener(
        "touchmove",
        function (event) {
          this._handleTouchMove(event);
        }.bind(this),
        { passive: true }
      );

      if (typeof this.responsive !== "undefined" && this.responsive.length) {
        this._checkResponsive();
      }
    });

    // setTimeout because it was not working in an umbraco website.
    setTimeout(() => {
      this._setSlideWidth();
      this._setSlideGap();
      this._checkIfNavNeeded();
      if (this.navNeeded) {
        this._checkLeftNav();
        this._checkRightNav();
      }
    }, 100);
  }

  _checkForErrors() {
    //console.log("error checker");
  }

  _setInitialSettings() {
    this.initialSettings = {
      slidesToShow: this.slidesToShow,
      slidesToScroll: parseInt(this.slidesToScroll),
    };
  }

  _setSlideWidth() {
    if (this.sliderWrap) {
      this.slideWidth =
        this.sliderWrap.offsetWidth / parseInt(this.slidesToShow) -
        parseInt(this.gap * 2);
      if (this.slides.length > 1) {
        this.slides.forEach((slide, index) => {
          slide.setAttribute("slideWidth", this.slideWidth);
          slide.setAttribute("index", index + 1);
        });
      } else {
        this.slides[0].forEach((slide, index) => {
          slide.setAttribute("slideWidth", this.slideWidth);
          slide.setAttribute("index", index + 1);
        });
      }
    }
  }

  _setSlideGap() {
    if (this.sliderWrap) {
      if (this.slides.length > 1) {
        this.slides.forEach((slide) => {
          slide.setAttribute("slideGap", this.gap);
        });
      } else {
        this.slides[0].forEach((slide) => {
          slide.setAttribute("slideGap", this.gap);
        });
      }
    }
  }

  _checkIfNavNeeded() {
    if (this.totalItems <= this.slidesToShow) {
      this.shadowRoot.querySelector(".nav-right").classList.add("hide");
      this.shadowRoot.querySelector(".nav-left").classList.add("hide");
      this.navNeeded = false;
    } else {
      this.shadowRoot.querySelector(".nav-right").classList.remove("hide");
      this.shadowRoot.querySelector(".nav-left").classList.remove("hide");
      this.navNeeded = true;
    }
  }

  _checkRightNav() {
    const endIndex =
      Math.ceil((this.totalItems - this.slidesToShow) / this.slidesToScroll) +
      1;
    if (this.activeIndex === endIndex) {
      this.rightDisabled = true;
      this.shadowRoot.querySelector(".nav-right").classList.add("disabled");
    } else {
      this.rightDisabled = false;
      this.shadowRoot.querySelector(".nav-right").classList.remove("disabled");
    }
  }

  _checkLeftNav() {
    if (this.activeIndex === 1) {
      this.leftDisabled = true;
      this.shadowRoot.querySelector(".nav-left").classList.add("disabled");
    } else {
      this.leftDisabled = false;
      this.shadowRoot.querySelector(".nav-left").classList.remove("disabled");
    }
  }

  _checkResponsive() {
    const windowWidth = window.innerWidth;
    for (const breakpoint of this.responsive) {
      const breakpointWidth = parseInt(breakpoint.breakpoint);
      if (windowWidth < breakpointWidth) {
        this.activeBreakpoint = breakpoint;
      }
    }

    if (
      this.activeBreakpoint &&
      windowWidth < this.activeBreakpoint.breakpoint
    ) {
      this.slidesToShow = parseInt(this.activeBreakpoint.settings.slidesToShow);
      this.slidesToScroll = parseInt(
        this.activeBreakpoint.settings.slidesToScroll
      );
      this._setSlideWidth();
      this._checkIfNavNeeded();
    } else {
      this.activeBreakpoint = {};
      this.slidesToScroll = this.initialSettings.slidesToScroll;
      this.slidesToShow = this.initialSettings.slidesToShow;
      this._setSlideWidth();
      this._checkIfNavNeeded();
    }
  }

  /**
   * Go to next slide
   */
  next() {
    this._checkRightNav();

    if (!this.rightDisabled) {
      this.activeIndex++;
      this.slideTotal -=
        (this.slideWidth + parseInt(this.gap * 2)) *
        parseInt(this.slidesToScroll);
      this._checkRightNav();
      this._checkLeftNav();
      this._createAndDispatchEvent();
    }
  }

  /**
   * Go to previous slide
   */
  prev() {
    this._checkLeftNav();
    if (!this.leftDisabled) {
      this.activeIndex--;
      this.slideTotal +=
        (this.slideWidth + parseInt(this.gap * 2)) *
        parseInt(this.slidesToScroll);
      this._checkLeftNav();
      this._checkRightNav();
      this._createAndDispatchEvent();
    }
  }

  goToSlide(index) {
    if (index > this.totalItems) {
      throw Error("This index does not exist!");
    }
    let slidesToSlide;
    if (parseInt(index) > this.activeIndex) {
      slidesToSlide = parseInt(index) - this.activeIndex;
      this.activeIndex = parseInt(index);
      this.slideTotal -=
        (this.slideWidth + parseInt(this.gap * 2)) *
        parseInt(slidesToSlide * this.slidesToScroll);
    } else {
      slidesToSlide = this.activeIndex - parseInt(index);
      this.activeIndex = parseInt(index);
      this.slideTotal +=
        (this.slideWidth + parseInt(this.gap * 2)) *
        parseInt(slidesToSlide * this.slidesToScroll);
    }

    this._checkLeftNav();
    this._checkRightNav();
    this._createAndDispatchEvent();
  }

  _createAndDispatchEvent() {
    this.slideChangedEvent = new CustomEvent("tp-slide-changed", {
      detail: {
        element: this,
        index: this.activeIndex,
        activeBreakpoint: this.activeBreakpoint,
        rightDisabled: this.rightDisabled,
        leftDisabled: this.leftDisabled,
      },
    });

    this.dispatchEvent(this.slideChangedEvent);
  }

  _getTouches(evt) {
    return evt.touches || evt.originalEvent.touches;
  }

  _handleTouchStart(evt) {
    const firstTouch = this._getTouches(evt)[0];
    this.xPosition = firstTouch.clientX;
    this.yPosition = firstTouch.clientY;
  }

  _handleTouchMove(evt) {
    if (!this.xPosition) {
      return;
    }

    const xUp = evt.touches[0].clientX;
    const yUp = evt.touches[0].clientY;

    const xDiff = this.xPosition - xUp;
    const yDiff = this.yPosition - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      /*most significant*/
      if (xDiff > 5) {
        this.next();
      } else {
        this.prev();
      }
    } else {
      return;
    }
    /* reset values */
    this.xPosition = null;
    this.yPosition = null;
  }

  static get styles() {
    return [
      super.styles,
      css`
        .container {
          width: var(--slider-container-width, 100%);
          position: relative;
          display: var(--slider-container-display, flex);
          flex-wrap: wrap;
          flex-direction: var(--slider-container-flex-direction, row);
          justify-content: var(--slider-container-justify-content, flex-end);
        }

        .slider-wrap {
          position: relative;
          overflow: hidden;
          z-index: 1;
          list-style: none;
          width: var(--slider-width, 100%);
          height: var(--slider-height, 450px);
          margin-bottom: var(--slider-wrap-margin-bottom, 1rem);
        }

        .slides {
          position: relative;
          z-index: 0;
          height: 100%;
          will-change: transform;
          display: inline-flex;
          width: var(--slider-slides-width, 100%);
          transition: transform 0.3s ease-in-out;
          margin: 0 auto;
        }

        .slider-nav {
          position: absolute;
          left: var(--slider-nav-position-left, auto);
          right: var(--slider-nav-position-right, 0);
          top: var(--slider-nav-position-top, auto);
          bottom: var(--slider-nav-position-bottom, 0);
          display: flex;
          justify-content: var(--slider-nav-justify-content, unset);
          margin: var(--slider-nav-margin, 0);
        }

        .nav-right.disabled {
          background-color: var(
            --slider-nav-right-disabled-background,
            #cecece
          );
          border-color: var(--slider-nav-right-disabled-border-color, #cecece);
          cursor: not-allowed;
        }

        .nav-left.disabled {
          background-color: var(--slider-nav-left-disabled-background, #cecece);
          border-color: var(--slider-nav-left-disabled-border-color, #cecece);
          cursor: not-allowed;
        }

        .nav-left.disabled ::slotted([slot="sliderLeft"]) {
          color: var(--slider-nav-left-disabled-color, white) !important;
          fill: var(--slider-nav-left-disabled-color, white) !important;
        }

        .nav-right.disabled ::slotted([slot="sliderRight"]) {
          color: var(--slider-nav-right-disabled-color, white) !important;
          fill: var(--slider-nav-right-disabled-color, white) !important;
        }

        .nav-left,
        .nav-right {
          transition: background-color 0.3s, color 0.3s;
        }

        .nav-left {
          left: var(--slider-nav-left-position-left, 0);
          right: var(--slider-nav-left-position-right, auto);
          top: var(--slider-nav-left-position-top, 0);
          bottom: var(--slider-nav-left-position-bottom, auto);
          margin: var(--slider-nav-left-margin, 0);
        }

        .nav-right {
          left: var(--slider-nav-right-position-left, auto);
          right: var(--slider-nav-right-position-right, 0);
          top: var(--slider-nav-right-position-top, 0);
          bottom: var(--slider-nav-right-position-bottom, auto);
          margin: var(--slider-nav-right-margin, 0);
        }

        .nav {
          width: var(--slider-nav-width, 50px);
          height: var(--slider-nav-height, 50px);
          background-color: var(--slider-nav-background, white);
          border: var(--slider-nav-border, none);
          border-radius: var(--slider-nav-border-radius, 0);
          color: var(--slider-nav-color, black);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease-in-out;
          position: var(--slider-nav-position, relative);
          z-index: 1;
        }

        .nav:hover,
        .nav:focus {
          background-color: var(--slider-nav-hover-background, white);
          border: var(--slider-nav-hover-border, none);
          color: var(--slider-nav-hover-color, #333);
        }

        .nav.disabled:hover,
        .nav.disabled:focus {
          background-color: var(
            --slider-disabled-nav-hover-background,
            #cecece
          );
          border: var(--slider-disabled-nav-hover-border, none);
        }

        .nav:hover ::slotted([slot="sliderRight"]),
        .nav:focus ::slotted([slot="sliderRight"]) {
          color: var(--slider-nav-hover-right-color, white) !important;
          fill: var(--slider-nav-hover-right-color, white) !important;
        }

        .nav:hover ::slotted([slot="sliderLeft"]),
        .nav:focus ::slotted([slot="sliderLeft"]) {
          color: var(--slider-nav-hover-left-color, white) !important;
          fill: var(--slider-nav-hover-left-color, white) !important;
        }

        .nav.disabled:hover ::slotted([slot="sliderRight"]),
        .nav.disabled:focus ::slotted([slot="sliderRight"]) {
          color: var(
            --slider-disabled-nav-hover-right-color,
            #cecece
          ) !important;
          fill: var(
            --slider-disabled-nav-hover-right-color,
            #cecece
          ) !important;
        }

        .nav.disabled:hover ::slotted([slot="sliderLeft"]),
        .nav.disabled:focus ::slotted([slot="sliderLeft"]) {
          color: var(
            --slider-disabled-nav-hover-left-color,
            #cecece
          ) !important;
          fill: var(--slider-disabled-nav-hover-left-color, #cecece) !important;
        }

        .hide {
          display: none;
        }
      `,
    ];
  }
}

window.customElements.get("true-slider") ||
  window.customElements.define("true-slider", TrueSlider);
